import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Modal, Form, Input, Select, message, Tag, Popconfirm, InputNumber, Typography, Divider, Checkbox, Row, Col, Collapse, Switch, Tooltip, AutoComplete } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined, SettingOutlined, ShoppingCartOutlined, HddOutlined, DesktopOutlined, ApiOutlined, InfoCircleOutlined } from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';
import apiClient from '../../../shared/services/apiClient';
import { CPU_MODELS, CPU_MODEL_RE } from '../../../shared/components/evm/vmdetail/SettingsTab';
import { useTranslation } from '../../../shared/i18n';
import I18nFieldTabs, { parseI18n, cleanI18n } from '../../components/I18nFieldTabs';
import CatalogTextsButton from '../../components/CatalogTextsButton';
import { currencySymbol, PLATFORM_CURRENCY } from '../../../shared/utils/money';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;
const VDS_DEFAULTS = { unit: '', numa: 'preferred', emulator_shared: true, hugepages: false, hugepage_kb: 2048, node_tags: [] };
const parseVds = (v) => {
    let o = v;
    if (typeof v === 'string') { try { o = JSON.parse(v); } catch (e) { o = null; } }
    return { ...VDS_DEFAULTS, ...(o && typeof o === 'object' ? o : {}) };
};

const ProductManagement = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [images, setImages] = useState([]);
    const [nodes, setNodes] = useState([]);
    const [zones, setZones] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
    const [modalVisible, setModalVisible] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [filters, setFilters] = useState({ keyword: '' });
    const [configOptions, setConfigOptions] = useState(null);
    const [showDeviceConfig, setShowDeviceConfig] = useState(false);
    const [showAddonConfig, setShowAddonConfig] = useState(false);
    const [selectedNodes, setSelectedNodes] = useState([]);
    const [cpuOpts, setCpuOpts] = useState(null);
    useEffect(() => {
        if (!selectedNodes.length) { setCpuOpts(null); return; }
        apiClient.get('/api/evm/admin/products/cpu-options', { params: { node_ids: selectedNodes.join(',') } })
            .then((r) => setCpuOpts(r.data?.status === 'success' ? r.data.data : null))
            .catch(() => setCpuOpts(null));
    }, [selectedNodes]);
    const [form] = Form.useForm();
    const saleType = Form.useWatch('sale_type', form);

    const categories = [
        { value: 'vps', label: 'VPS', color: 'blue' },
        { value: 'cloud', label: 'Cloud Server', color: 'green' },
        { value: 'dedicated', label: 'Dedicated', color: 'purple' },
    ];

    useEffect(() => {
        fetchProducts();
        fetchRelatedData();
        fetchConfigOptions();
    }, [pagination.current, filters]);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await evmAdminService.getProductList({
                page: pagination.current,
                per_page: pagination.pageSize,
                ...filters,
            });
            if (response.status === 'success') {
                setProducts(response.data?.data || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.data?.total || 0,
                }));
            }
        } catch (error) {
            message.error('Failed to fetch products');
        }
        setLoading(false);
    };

    // Helper to extract array from paginated response
    const extractArray = (data, ...keys) => {
        if (Array.isArray(data)) return data;
        for (const key of keys) {
            if (Array.isArray(data?.[key])) return data[key];
        }
        if (Array.isArray(data?.data)) return data.data;
        return [];
    };

    const fetchRelatedData = async () => {
        try {
            const [templatesRes, imagesRes, nodesRes, zonesRes] = await Promise.all([
                evmAdminService.getTemplateList({ per_page: 100 }),
                evmAdminService.getImageList({ per_page: 100 }),
                evmAdminService.getNodeList({ per_page: 100 }),
                evmAdminService.getZoneList({ per_page: 100 }),
            ]);
            if (templatesRes.status === 'success') setTemplates(extractArray(templatesRes.data, 'templates'));
            if (imagesRes.status === 'success') setImages(extractArray(imagesRes.data, 'images'));
            if (nodesRes.status === 'success') setNodes(extractArray(nodesRes.data, 'nodes'));
            if (zonesRes.status === 'success') setZones(extractArray(zonesRes.data, 'zones'));
        } catch (error) {
            console.error('Failed to fetch related data');
        }
    };

    const fetchConfigOptions = async () => {
        try {
            const response = await evmAdminService.getProductConfigOptions();
            if (response.status === 'success') {
                setConfigOptions(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch config options');
        }
    };

    const handleCreate = () => {
        setEditingProduct(null);
        setShowDeviceConfig(false);
        setShowAddonConfig(false);
        setSelectedNodes([]);
        form.resetFields();
        const deviceDefaults = configOptions?.device_defaults || {};
        form.setFieldsValue({
            status: 'enable',
            elastic: 1,
            sold: 0,
            limit: 0,
            cpu_model: '',
            nested_virt: false,
            sale_type: 'dedicated',
            vds: VDS_DEFAULTS,
            // Device config defaults
            ...deviceDefaults,
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingProduct(record);
        // Check for device/addon config
        const hasDeviceConfig = record.sys_disk_driver || record.net_driver || record.sys_disk_pool;
        const hasAddonConfig = record.addon_config;
        setShowDeviceConfig(hasDeviceConfig);
        setShowAddonConfig(hasAddonConfig);
        // Parse comma-separated IDs to arrays
        const templateIds = record.template ? record.template.split(',').map(Number) : [];
        const imageIds = record.image ? record.image.split(',').map(Number) : [];
        const nodeIds = record.node_id ? record.node_id.split(',').map(Number) : [];
        setSelectedNodes(nodeIds);
        // Parse addon_config JSON
        let addonConfig = configOptions?.addon_defaults || {};
        try {
            if (record.addon_config) {
                addonConfig = typeof record.addon_config === 'string'
                    ? JSON.parse(record.addon_config)
                    : record.addon_config;
            }
        } catch (e) {
            console.error('Failed to parse addon_config');
        }

        const deviceDefaults = configOptions?.device_defaults || {};
        form.setFieldsValue({
            ...record,
            i18n: parseI18n(record.i18n),
            template: templateIds,
            image: imageIds,
            node_id: nodeIds,
            vds: parseVds(record.vds),
            // Ensure device config fields have values
            sys_disk_driver: record.sys_disk_driver || deviceDefaults.sys_disk_driver,
            sys_disk_cache: record.sys_disk_cache || deviceDefaults.sys_disk_cache,
            sys_disk_io: record.sys_disk_io || deviceDefaults.sys_disk_io,
            data_disk_driver: record.data_disk_driver || deviceDefaults.data_disk_driver,
            data_disk_cache: record.data_disk_cache || deviceDefaults.data_disk_cache,
            data_disk_io: record.data_disk_io || deviceDefaults.data_disk_io,
            net_driver: record.net_driver || deviceDefaults.net_driver,
            display_type: record.display_type || deviceDefaults.display_type,
            video_model: record.video_model || deviceDefaults.video_model,
            vnc_keymap: record.vnc_keymap || deviceDefaults.vnc_keymap,
            cpu_mode: record.cpu_mode || deviceDefaults.cpu_mode,
            machine_type: record.machine_type || deviceDefaults.machine_type,
            // Addon config
            addon_enable: addonConfig.enable_addon,
            addon_cpu_enable: addonConfig.cpu?.enable,
            addon_cpu_max: addonConfig.cpu?.max,
            addon_cpu_price: addonConfig.cpu?.price_per_unit,
            addon_memory_enable: addonConfig.memory?.enable,
            addon_memory_max: addonConfig.memory?.max,
            addon_memory_price: addonConfig.memory?.price_per_unit,
            addon_disk_enable: addonConfig.disk?.enable,
            addon_disk_max: addonConfig.disk?.max,
            addon_disk_price: addonConfig.disk?.price_per_unit,
            addon_bandwidth_enable: addonConfig.bandwidth?.enable,
            addon_bandwidth_max: addonConfig.bandwidth?.max,
            addon_bandwidth_price: addonConfig.bandwidth?.price_per_unit,
            addon_ip_enable: addonConfig.ip?.enable,
            addon_ip_max: addonConfig.ip?.max,
            addon_ip_price: addonConfig.ip?.price_per_unit,
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const response = await evmAdminService.deleteProduct(id);
            if (response.status === 'success') {
                message.success('Product deleted successfully');
                fetchProducts();
            } else {
                message.error(response.msg || 'Failed to delete product');
            }
        } catch (error) {
            message.error('Failed to delete product');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            // Build addon_config object
            const addonConfig = {
                enable_addon: values.addon_enable || false,
                cpu: {
                    enable: values.addon_cpu_enable || false,
                    min: 0,
                    max: values.addon_cpu_max || 8,
                    step: 1,
                    price_per_unit: values.addon_cpu_price || 0,
                },
                memory: {
                    enable: values.addon_memory_enable || false,
                    min: 0,
                    max: values.addon_memory_max || 16384,
                    step: 512,
                    price_per_unit: values.addon_memory_price || 0,
                },
                disk: {
                    enable: values.addon_disk_enable || false,
                    min: 0,
                    max: values.addon_disk_max || 500,
                    step: 10,
                    price_per_unit: values.addon_disk_price || 0,
                },
                bandwidth: {
                    enable: values.addon_bandwidth_enable || false,
                    min: 0,
                    max: values.addon_bandwidth_max || 100,
                    step: 1,
                    price_per_unit: values.addon_bandwidth_price || 0,
                },
                ip: {
                    enable: values.addon_ip_enable || false,
                    min: 0,
                    max: values.addon_ip_max || 5,
                    price_per_unit: values.addon_ip_price || 0,
                },
            };

            // Convert arrays to comma-separated strings
            const submitData = {
                ...values,
                i18n: cleanI18n(values.i18n),
                template: Array.isArray(values.template) ? values.template.join(',') : values.template,
                image: Array.isArray(values.image) ? values.image.join(',') : values.image,
                node_id: Array.isArray(values.node_id) ? values.node_id.join(',') : values.node_id,
                addon_config: showAddonConfig ? addonConfig : null,
            };
            if (values.sale_type === 'vds') submitData.vds = { ...parseVds(form.getFieldValue('vds')), ...(values.vds || {}) };
            else delete submitData.vds;

            // Remove addon form fields from submitData
            const addonFields = ['addon_enable', 'addon_cpu_enable', 'addon_cpu_max', 'addon_cpu_price',
                'addon_memory_enable', 'addon_memory_max', 'addon_memory_price',
                'addon_disk_enable', 'addon_disk_max', 'addon_disk_price',
                'addon_bandwidth_enable', 'addon_bandwidth_max', 'addon_bandwidth_price',
                'addon_ip_enable', 'addon_ip_max', 'addon_ip_price'];
            addonFields.forEach(f => delete submitData[f]);

            // Remove device config if not shown
            if (!showDeviceConfig) {
                const deviceFields = ['sys_disk_driver', 'sys_disk_cache', 'sys_disk_io',
                    'data_disk_driver', 'data_disk_cache', 'data_disk_io',
                    'net_driver', 'display_type', 'video_model', 'vnc_keymap',
                    'cpu_mode', 'machine_type', 'sys_disk_pool', 'data_disk_pool'];
                deviceFields.forEach(f => delete submitData[f]);
            }

            const response = editingProduct
                ? await evmAdminService.updateProduct(editingProduct.id, submitData)
                : await evmAdminService.createProduct(submitData);

            if (response.status === 'success') {
                message.success(editingProduct ? 'Product updated' : 'Product created');
                setModalVisible(false);
                fetchProducts();
            } else {
                message.error(response.msg || 'Operation failed');
            }
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 60,
        },
        {
            title: 'Product',
            dataIndex: 'name',
            render: (text, record) => (
                <Space direction="vertical" size={0}>
                    <span style={{ fontWeight: 500 }}>{text}{record.sale_type === 'vds' && <Tag color="purple" style={{ marginInlineStart: 6 }}>VDS</Tag>}</span>
                    {record.key && <Text type="secondary" style={{ fontSize: 12 }}>{record.key}</Text>}
                </Space>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            width: 100,
            render: (cat) => {
                const catInfo = categories.find(c => c.value === cat);
                return <Tag color={catInfo?.color || 'default'}>{catInfo?.label || cat}</Tag>;
            },
        },
        {
            title: 'Templates',
            dataIndex: 'template',
            width: 120,
            render: (tpl) => {
                if (!tpl) return '-';
                const ids = tpl.split(',');
                return <Tag>{ids.length} template(s)</Tag>;
            },
        },
        {
            title: 'Images',
            dataIndex: 'image',
            width: 100,
            render: (img) => {
                if (!img) return '-';
                const ids = img.split(',');
                return <Tag>{ids.length} image(s)</Tag>;
            },
        },
        {
            title: 'Nodes',
            dataIndex: 'node_id',
            width: 100,
            render: (nodeIds) => {
                if (!nodeIds) return '-';
                const ids = nodeIds.split(',');
                return <Tag>{ids.length} node(s)</Tag>;
            },
        },
        {
            title: 'Addon',
            dataIndex: 'addon_config',
            width: 80,
            render: (config) => {
                if (!config) return <Tag>No</Tag>;
                try {
                    const parsed = typeof config === 'string' ? JSON.parse(config) : config;
                    return parsed.enable_addon ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>;
                } catch {
                    return <Tag>No</Tag>;
                }
            },
        },
        {
            title: 'Sold / Limit',
            width: 100,
            render: (_, record) => (
                <span>{record.sold} / {record.limit === 0 ? '∞' : record.limit}</span>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 80,
            render: (status) => (
                <Tag color={status === 'enable' ? 'success' : 'default'}>
                    {status === 'enable' ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title="Delete this product?"
                        description="This action cannot be undone."
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // Get filtered storage pools based on selected nodes
    const getFilteredStoragePools = () => {
        const allPools = configOptions?.storage_pools || [];
        if (selectedNodes.length === 0) return allPools;
        return allPools.filter(pool => selectedNodes.includes(pool.node_id));
    };

    // Get node name for storage pool display
    const getNodeName = (nodeId) => {
        const node = nodes.find(n => n.id === nodeId);
        return node ? node.name : `Node ${nodeId}`;
    };

    const renderDeviceConfig = () => {
        if (!configOptions) return null;
        const opts = configOptions.device_options || {};
        const filteredPools = getFilteredStoragePools();
        return (
            <Collapse defaultActiveKey={['storage', 'disk', 'network', 'display', 'cpu']} ghost>
                <Panel header={<><HddOutlined /> Storage Pool Selection</>} key="storage">
                    {selectedNodes.length === 0 && (
                        <Text type="warning" style={{ display: 'block', marginBottom: 12 }}>
                            Please select nodes first to see available storage pools.
                        </Text>
                    )}
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="sys_disk_pool" label="System Disk Pool">
                                <Select
                                    allowClear
                                    placeholder="Select storage pool for system disk"
                                    disabled={selectedNodes.length === 0}
                                >
                                    {filteredPools.map(pool => (
                                        <Option key={pool.id} value={pool.id}>
                                            [{getNodeName(pool.node_id)}] {pool.name} ({pool.size}GB - {pool.path})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="data_disk_pool" label="Data Disk Pool">
                                <Select
                                    allowClear
                                    placeholder="Select storage pool for data disk"
                                    disabled={selectedNodes.length === 0}
                                >
                                    {filteredPools.map(pool => (
                                        <Option key={pool.id} value={pool.id}>
                                            [{getNodeName(pool.node_id)}] {pool.name} ({pool.size}GB - {pool.path})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Note: When multiple nodes are selected, choose storage pools from each node or leave empty for auto-selection.
                    </Text>
                </Panel>

                <Panel header={<><HddOutlined /> Disk Configuration (Override Template)</>} key="disk">
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="sys_disk_driver" label="System Disk Driver">
                                <Select allowClear placeholder="Use template default">
                                    {opts.disk_drivers?.map(d => (
                                        <Option key={d} value={d}>{d.toUpperCase()}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="sys_disk_cache" label="System Disk Cache">
                                <Select allowClear placeholder="Use template default">
                                    {opts.disk_cache_modes?.map(c => (
                                        <Option key={c} value={c}>{c}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="sys_disk_io" label="System Disk IO">
                                <Select allowClear placeholder="Use template default">
                                    {opts.disk_io_modes?.map(io => (
                                        <Option key={io} value={io}>{io}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="data_disk_driver" label="Data Disk Driver">
                                <Select allowClear placeholder="Use template default">
                                    {opts.disk_drivers?.map(d => (
                                        <Option key={d} value={d}>{d.toUpperCase()}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="data_disk_cache" label="Data Disk Cache">
                                <Select allowClear placeholder="Use template default">
                                    {opts.disk_cache_modes?.map(c => (
                                        <Option key={c} value={c}>{c}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="data_disk_io" label="Data Disk IO">
                                <Select allowClear placeholder="Use template default">
                                    {opts.disk_io_modes?.map(io => (
                                        <Option key={io} value={io}>{io}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Panel>

                <Panel header={<><ApiOutlined /> Network Configuration</>} key="network">
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="net_driver" label="Network Driver">
                                <Select allowClear placeholder="Use template default">
                                    {opts.net_drivers?.map(d => (
                                        <Option key={d} value={d}>{d}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Panel>

                <Panel header={<><DesktopOutlined /> Display Configuration</>} key="display">
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="display_type" label="Display Type">
                                <Select allowClear placeholder="Use template default">
                                    {opts.display_types?.map(d => (
                                        <Option key={d} value={d}>{d.toUpperCase()}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="video_model" label="Video Model">
                                <Select allowClear placeholder="Use template default">
                                    {opts.video_models?.map(v => (
                                        <Option key={v} value={v}>{v}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="vnc_keymap" label="VNC Keymap">
                                <Select allowClear placeholder="Use template default">
                                    {opts.vnc_keymaps?.map(k => (
                                        <Option key={k} value={k}>{k}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Panel>

                <Panel header={<><SettingOutlined /> CPU & Machine</>} key="cpu">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="cpu_mode" label="CPU Mode">
                                <Select allowClear placeholder="Use template default">
                                    {opts.cpu_modes?.map(m => (
                                        <Option key={m} value={m}>{m}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="machine_type" label="Machine Type">
                                <Input placeholder="e.g., pc-q35-6.2" allowClear />
                            </Form.Item>
                        </Col>
                    </Row>
                </Panel>
            </Collapse>
        );
    };

    const renderAddonConfig = () => (
        <div>
            <Form.Item name="addon_enable" valuePropName="checked">
                <Checkbox>启用附加购买</Checkbox>
            </Form.Item>

            <Row gutter={16}>
                <Col span={8}>
                    <Card size="small" title="CPU (核心)">
                        <Form.Item name="addon_cpu_enable" valuePropName="checked">
                            <Checkbox>允许附加</Checkbox>
                        </Form.Item>
                        <Form.Item name="addon_cpu_max" label="最大附加">
                            <InputNumber min={0} max={64} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="addon_cpu_price" label="单价/核">
                            <InputNumber min={0} style={{ width: '100%' }} prefix={currencySymbol(PLATFORM_CURRENCY)} />
                        </Form.Item>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card size="small" title="内存 (MB)">
                        <Form.Item name="addon_memory_enable" valuePropName="checked">
                            <Checkbox>允许附加</Checkbox>
                        </Form.Item>
                        <Form.Item name="addon_memory_max" label="最大附加">
                            <InputNumber min={0} max={65536} step={512} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="addon_memory_price" label="单价/512MB">
                            <InputNumber min={0} style={{ width: '100%' }} prefix={currencySymbol(PLATFORM_CURRENCY)} />
                        </Form.Item>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card size="small" title="磁盘 (GB)">
                        <Form.Item name="addon_disk_enable" valuePropName="checked">
                            <Checkbox>允许附加</Checkbox>
                        </Form.Item>
                        <Form.Item name="addon_disk_max" label="最大附加">
                            <InputNumber min={0} max={2000} step={10} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="addon_disk_price" label="单价/10GB">
                            <InputNumber min={0} style={{ width: '100%' }} prefix={currencySymbol(PLATFORM_CURRENCY)} />
                        </Form.Item>
                    </Card>
                </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={8}>
                    <Card size="small" title="带宽 (Mbps)">
                        <Form.Item name="addon_bandwidth_enable" valuePropName="checked">
                            <Checkbox>允许附加</Checkbox>
                        </Form.Item>
                        <Form.Item name="addon_bandwidth_max" label="最大附加">
                            <InputNumber min={0} max={1000} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="addon_bandwidth_price" label="单价/Mbps">
                            <InputNumber min={0} style={{ width: '100%' }} prefix={currencySymbol(PLATFORM_CURRENCY)} />
                        </Form.Item>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card size="small" title="IP地址">
                        <Form.Item name="addon_ip_enable" valuePropName="checked">
                            <Checkbox>允许附加</Checkbox>
                        </Form.Item>
                        <Form.Item name="addon_ip_max" label="最大附加">
                            <InputNumber min={0} max={10} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="addon_ip_price" label="单价/IP">
                            <InputNumber min={0} style={{ width: '100%' }} prefix={currencySymbol(PLATFORM_CURRENCY)} />
                        </Form.Item>
                    </Card>
                </Col>
            </Row>
        </div>
    );

    return (
        <div>
            <Card>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={4} style={{ margin: 0 }}>产品管理</Title>
                    <Space>
                        <Input
                            placeholder="Search..."
                            prefix={<SearchOutlined />}
                            value={filters.keyword}
                            onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                            style={{ width: 200 }}
                            allowClear
                        />
                        <Select allowClear placeholder={t('Sale type')} value={filters.sale_type} style={{ width: 150 }}
                            onChange={(v) => setFilters({ ...filters, sale_type: v })}
                            options={[{ value: 'dedicated', label: t('Dedicated') }, { value: 'overcommit', label: t('Overcommit (promo)') }, { value: 'vds', label: 'VDS' }]} />
                        <Button icon={<ReloadOutlined />} onClick={fetchProducts}>Refresh</Button>
                        <CatalogTextsButton
                            title={t('Product categories')}
                            canCreate
                            load={async () => {
                                const r = await apiClient.get('/api/evm/admin/product-categories');
                                if (r.data?.status !== 'success') throw { error: r.data?.msg };
                                return r.data.data?.categories || [];
                            }}
                            save={async (row, v) => {
                                const r = await apiClient.post(row ? `/api/evm/admin/product-categories/${row.id}` : '/api/evm/admin/product-categories', v);
                                if (r.data?.status !== 'success') throw { error: r.data?.msg };
                            }}
                        />
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                            Add Product
                        </Button>
                    </Space>
                </div>

                <Table
                    columns={columns}
                    dataSource={products}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} products`,
                        onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize }),
                    }}
                />
            </Card>

            <Modal
                title={editingProduct ? 'Edit Product' : 'Create Product'}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                width={1000}
                style={{ top: 20 }}
            >
                <Form form={form} layout="vertical">
                    <I18nFieldTabs
                        fields={[{ key: 'name', label: 'Product Name' }, { key: 'description', label: 'Description', type: 'textarea' }]}
                        base={<>
                            <Form.Item
                                name="name"
                                label="Product Name"
                                rules={[{ required: true, message: 'Please enter product name' }]}
                            >
                                <Input placeholder="e.g., Basic VPS" />
                            </Form.Item>
                            <Form.Item
                                name="description"
                                label="Description"
                            >
                                <TextArea rows={3} placeholder="Product description..." />
                            </Form.Item>
                        </>}
                    />
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="key"
                                label="Product Key"
                            >
                                <Input placeholder="e.g., basic-vps" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="category"
                                label="Category"
                                rules={[{ required: true, message: 'Please select category' }]}
                            >
                                <Select placeholder="Select category">
                                    {categories.map(c => (
                                        <Option key={c.value} value={c.value}>{c.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="zone_id"
                                label="Zone"
                            >
                                <Select placeholder="Select zone" allowClear>
                                    {zones.map(z => (
                                        <Option key={z.id} value={z.id}>{z.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="sort"
                                label="Sort Order"
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>Configuration</Divider>

                    <Form.Item
                        name="template"
                        label="Available Templates"
                        rules={[{ required: true, message: 'Please select at least one template' }]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select templates"
                            optionFilterProp="children"
                        >
                            {templates.map(t => (
                                <Option key={t.id} value={t.id}>
                                    {t.name} ({t.type === 'elastic' ? 'Elastic' : 'Static'})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="image"
                        label="Available Images"
                        rules={[{ required: true, message: 'Please select at least one image' }]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select images"
                            optionFilterProp="children"
                        >
                            {images.map(i => (
                                <Option key={i.id} value={i.id}>
                                    {i.name} ({i.type})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="node_id"
                        label="Available Nodes"
                        rules={[{ required: true, message: 'Please select at least one node' }]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select nodes"
                            optionFilterProp="children"
                            onChange={(values) => setSelectedNodes(values || [])}
                        >
                            {nodes.map(n => (
                                <Option key={n.id} value={n.id}>
                                    {n.name} ({n.hostname})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Divider>Sales Settings</Divider>

                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item
                                name="elastic"
                                label="Elastic Config"
                                valuePropName="checked"
                            >
                                <Checkbox>Enable</Checkbox>
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="sold"
                                label="Sold Count"
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="limit"
                                label="Stock Limit (0=unlimited)"
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="status"
                                label="Status"
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Option value="enable">Active</Option>
                                    <Option value="disable">Inactive</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="cpu_model"
                                label="CPU 型号"
                                extra={Object.values(cpuOpts?.host_models || {}).length ? `节点 CPU：${[...new Set(Object.values(cpuOpts.host_models))].join(' / ')}` : null}
                                tooltip="host-passthrough 性能最好，但只能在相同 CPU 的节点间迁移"
                                rules={[{ pattern: CPU_MODEL_RE, message: '型号无效' }]}
                            >
                                <AutoComplete allowClear placeholder="IvyBridge" options={(cpuOpts?.reported ? cpuOpts.models : CPU_MODELS).map((m) => ({ value: m }))} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="nested_virt" label="嵌套虚拟化" valuePropName="checked" tooltip="套餐内实例默认值（可按实例覆盖），重启后生效">
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="sale_type" label={t('Sale type')} tooltip={t('Overcommit plans only run on overcommit/mixed nodes; hidden from users')}>
                                <Select onChange={(v) => { if (v === 'vds' && !editingProduct) form.setFieldsValue({ nested_virt: true, cpu_model: '' }); }}
                                    options={[{ value: 'dedicated', label: t('Dedicated') }, { value: 'overcommit', label: t('Overcommit (promo)') }, { value: 'vds', label: t('VDS (dedicated physical cores)') }]} />
                            </Form.Item>
                        </Col>
                    </Row>

                    {saleType === 'vds' && (
                        <Card size="small" title="VDS" style={{ marginBottom: 16 }}>
                            <Form.Item name={['vds', 'unit']} label={t('Allocation unit')} style={{ marginBottom: 8 }}>
                                <Select style={{ maxWidth: 240 }} options={[{ value: '', label: t('Follow node') }, { value: 'core', label: t('Whole core') }, { value: 'thread', label: t('Thread') }]} />
                            </Form.Item>
                            <Collapse ghost size="small" items={[{
                                key: 'adv', label: t('Advanced'), forceRender: true, children: (
                                    <Row gutter={16}>
                                        <Col span={8}>
                                            <Form.Item name={['vds', 'numa']} label="NUMA">
                                                <Select options={[{ value: 'preferred', label: 'preferred' }, { value: 'strict', label: 'strict' }, { value: 'none', label: t('Unlimited') }]} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item name={['vds', 'emulator_shared']} label={t('Emulator on shared cores')} valuePropName="checked" tooltip={t('Emulator/IO threads may use shared cores')}>
                                                <Switch />
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item label={t('Hugepages')}>
                                                <Space>
                                                    <Form.Item name={['vds', 'hugepages']} valuePropName="checked" noStyle><Switch /></Form.Item>
                                                    <Form.Item name={['vds', 'hugepage_kb']} noStyle>
                                                        <Select style={{ width: 90 }} options={[{ value: 2048, label: '2 MB' }, { value: 1048576, label: '1 GB' }]} />
                                                    </Form.Item>
                                                </Space>
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item name={['vds', 'node_tags']} label={t('Node tags')}>
                                                <Select mode="tags" open={false} tokenSeparators={[',', ' ']} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                ),
                            }]} />
                        </Card>
                    )}

                    <Divider>
                        <Space>
                            <SettingOutlined />
                            Device Configuration Override
                            <Switch
                                size="small"
                                checked={showDeviceConfig}
                                onChange={setShowDeviceConfig}
                            />
                            <Tooltip title="Override device settings from template">
                                <InfoCircleOutlined />
                            </Tooltip>
                        </Space>
                    </Divider>

                    {showDeviceConfig && renderDeviceConfig()}

                    <Divider>
                        <Space>
                            <ShoppingCartOutlined />
                            Addon Purchase Configuration
                            <Switch
                                size="small"
                                checked={showAddonConfig}
                                onChange={setShowAddonConfig}
                            />
                            <Tooltip title="Allow users to purchase additional resources">
                                <InfoCircleOutlined />
                            </Tooltip>
                        </Space>
                    </Divider>

                    {showAddonConfig && renderAddonConfig()}
                </Form>
            </Modal>
        </div>
    );
};

export default ProductManagement;
